import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Link2, Plus, Copy, Trash2, PowerOff, Power, MessageCircle, Loader2,
} from 'lucide-react';

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
  : '—';

export default function AuditLinksAdmin() {
  const { API } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const baseUrl = window.location.origin;

  const buildUrl = (token) => `${baseUrl}/audit/${token}`;

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/audit-links`);
      setLinks(data || []);
    } catch (e) {
      toast.error('Failed to load audit links');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return toast.error('Give the link a name (e.g. "Supervisor Rahul")');
    setCreating(true);
    try {
      await axios.post(`${API}/audit-links`, { name });
      toast.success('Audit link created');
      setNewName('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (link) => {
    try {
      await axios.patch(`${API}/audit-links/${link.id}`, { is_active: !link.is_active });
      toast.success(link.is_active ? 'Link revoked' : 'Link reactivated');
      load();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm(`Delete "${link.name}" link permanently?`)) return;
    try {
      await axios.delete(`${API}/audit-links/${link.id}`);
      toast.success('Link deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const copyUrl = async (link) => {
    const url = buildUrl(link.token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.message(url);
    }
  };

  const shareOnWhatsApp = (link) => {
    const url = buildUrl(link.token);
    const text = encodeURIComponent(
      `Hi, please use this secure link to submit vehicle audits (Allotment / Submission / Repair / Parking): ${url}`
    );
    // Open wa.me without a specific number so the user picks the chat
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card data-testid="audit-links-admin">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Link2 className="w-5 h-5 text-slate-600" /> Audit Links
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Share permanent tokenised links so supervisors / vendors can submit Allotment, Submission, Repair, and Parking audits without logging in.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Create row */}
        <div className="flex items-end gap-2 border rounded-md p-3 bg-slate-50">
          <div className="flex-1">
            <Label className="text-xs text-slate-500">New link name</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder='e.g. "Supervisor Rahul", "Vendor ABC Motors"'
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              data-testid="audit-link-name-input"
            />
          </div>
          <Button onClick={handleCreate} disabled={creating} data-testid="audit-link-create-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Link
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Usage</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead className="text-right w-56">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-6">
                  No audit links yet. Create one above.
                </TableCell></TableRow>
              )}
              {links.map(link => {
                const url = buildUrl(link.token);
                return (
                  <TableRow key={link.id} data-testid={`audit-link-row-${link.id}`}>
                    <TableCell className="font-semibold">{link.name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono text-slate-700 truncate inline-block max-w-[280px] align-middle">
                        {url}
                      </code>
                    </TableCell>
                    <TableCell>
                      {link.is_active
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
                        : <Badge className="bg-rose-100 text-rose-700 border-rose-200">Revoked</Badge>}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{link.use_count || 0}</TableCell>
                    <TableCell className="text-xs text-slate-600">{fmtDate(link.last_used_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={() => copyUrl(link)} data-testid={`audit-link-copy-${link.id}`}>
                          <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                        </Button>
                        <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => shareOnWhatsApp(link)}
                                data-testid={`audit-link-wa-${link.id}`}>
                          <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleToggle(link)} title={link.is_active ? 'Revoke' : 'Reactivate'}
                                data-testid={`audit-link-toggle-${link.id}`}>
                          {link.is_active
                            ? <PowerOff className="w-3.5 h-3.5 text-rose-600" />
                            : <Power className="w-3.5 h-3.5 text-emerald-600" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(link)}
                                data-testid={`audit-link-delete-${link.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
